<template>
    <div id="productBulkUpdateModal" class="modal centered-modal">
        <div class="modal-dialog max-w-2xl my-auto rounded-xl shadow-lg">
            <div class="modal-header">
                <div class="flex items-center gap-2">
                    <span class="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-base">
                        <i class="lab lab-line-edit"></i>
                    </span>
                    <div>
                        <h3 class="modal-title font-semibold text-lg text-slate-800">
                            {{ $t('label.bulk_field_update') }}
                        </h3>
                        <p class="text-xs text-slate-500 leading-none">
                            {{ $t('label.bulk_field_update_desc') }}
                        </p>
                    </div>
                </div>
                <button type="button" class="modal-close fa-solid fa-xmark text-xl text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed" :disabled="isSubmitting" @click="reset"></button>
            </div>

            <div class="modal-body p-5">
                <form @submit.prevent="confirmSubmit" class="w-full">
                    <fieldset :disabled="isSubmitting" class="w-full">
                    <!-- PASO 1: SELECCION DE CAMPO -->
                    <div class="mb-4">
                        <label class="db-field-title required font-semibold text-xs uppercase tracking-wider text-slate-600 mb-1.5 block">
                            1. {{ $t('label.select_field') }}
                        </label>
                        <vue-select
                            class="db-field-control f-b-custom-select bg-white"
                            v-model="form.field"
                            :options="fieldOptions"
                            label-by="name"
                            value-by="id"
                            :searchable="true"
                            :closeOnSelect="true"
                            :clearOnClose="true"
                            placeholder="--"
                            search-placeholder="--"
                        />
                    </div>

                    <!-- PASO 2: OPERADOR Y NUEVO VALOR -->
                    <div class="mb-4" v-if="form.field">
                        <label class="db-field-title required font-semibold text-xs uppercase tracking-wider text-slate-600 mb-2 block">
                            2. {{ $t('label.operation_and_value') }}
                        </label>

                        <!-- Para campos numéricos -->
                        <div v-if="currentFieldType === 'numeric'" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label class="text-xs text-slate-500 mb-1 block">{{ $t('label.calculation_type') }}</label>
                                <vue-select
                                    class="db-field-control f-b-custom-select bg-white"
                                    v-model="form.operator"
                                    :options="numericOperators"
                                    label-by="name"
                                    value-by="id"
                                    :searchable="false"
                                    :closeOnSelect="true"
                                />
                            </div>
                            <div>
                                <label class="text-xs text-slate-500 mb-1 block">
                                     {{ $t('label.value') }} {{ isPercentageOperator ? '(%)' : '' }}
                                </label>
                                <div class="relative">
                                    <input
                                        type="number"
                                        step="any"
                                        v-model="form.value"
                                        class="db-field-control w-full bg-white border border-slate-300 rounded-lg py-2 px-3"
                                        :placeholder="isPercentageOperator ? '10' : '150.00'"
                                        required
                                    />
                                    <span v-if="isPercentageOperator" class="absolute right-3 top-2.5 text-xs text-slate-400 font-bold">%</span>
                                </div>
                            </div>
                        </div>

                        <!-- Para estados booleanos / enums -->
                        <div v-else-if="currentFieldType === 'enum_status'">
                            <label class="text-xs text-slate-500 mb-1 block">{{ $t('label.new_status') }}</label>
                            <vue-select
                                class="db-field-control f-b-custom-select bg-white"
                                v-model="form.value"
                                :options="statusOptions"
                                label-by="name"
                                value-by="id"
                                :searchable="false"
                                :closeOnSelect="true"
                            />
                        </div>

                        <div v-else-if="currentFieldType === 'enum_ask'">
                            <label class="text-xs text-slate-500 mb-1 block">{{ $t('label.select_option') }}</label>
                            <vue-select
                                class="db-field-control f-b-custom-select bg-white"
                                v-model="form.value"
                                :options="yesNoOptions"
                                label-by="name"
                                value-by="id"
                                :searchable="false"
                                :closeOnSelect="true"
                            />
                        </div>

                        <div v-else-if="currentFieldType === 'text'">
                            <label class="text-xs text-slate-500 mb-1 block">{{ $t('label.value') }}</label>
                            <input
                                type="text"
                                v-model="form.value"
                                class="db-field-control w-full bg-white border border-slate-300 rounded-lg py-2 px-3"
                                required
                            />
                        </div>
                    </div>

                    <!-- PASO 3: ALCANCE / SCOPE -->
                    <div class="mb-4" v-if="form.field">
                        <label class="db-field-title required font-semibold text-xs uppercase tracking-wider text-slate-600 mb-2 block">
                            3. {{ $t('label.scope') }}
                        </label>
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                            <button
                                type="button"
                                @click="form.scope_type = 'all'"
                                class="p-3 rounded-lg border text-xs font-medium transition flex flex-col items-center gap-1"
                                :class="form.scope_type === 'all' ? 'border-primary bg-primary/10 text-primary font-bold shadow-sm' : 'border-slate-200 text-slate-600 hover:bg-slate-50'"
                            >
                                <i class="fa-solid fa-globe text-sm"></i>
                                <span>{{ $t('label.scope_all') }}</span>
                            </button>

                            <button
                                type="button"
                                @click="form.scope_type = 'category'"
                                class="p-3 rounded-lg border text-xs font-medium transition flex flex-col items-center gap-1"
                                :class="form.scope_type === 'category' ? 'border-primary bg-primary/10 text-primary font-bold shadow-sm' : 'border-slate-200 text-slate-600 hover:bg-slate-50'"
                            >
                                <i class="fa-solid fa-folder-tree text-sm"></i>
                                <span>{{ $t('label.scope_category') }}</span>
                            </button>

                            <button
                                type="button"
                                @click="form.scope_type = 'brand'"
                                class="p-3 rounded-lg border text-xs font-medium transition flex flex-col items-center gap-1"
                                :class="form.scope_type === 'brand' ? 'border-primary bg-primary/10 text-primary font-bold shadow-sm' : 'border-slate-200 text-slate-600 hover:bg-slate-50'"
                            >
                                <i class="fa-solid fa-tag text-sm"></i>
                                <span>{{ $t('label.scope_brand') }}</span>
                            </button>

                            <button
                                type="button"
                                @click="form.scope_type = 'manual'"
                                class="p-3 rounded-lg border text-xs font-medium transition flex flex-col items-center gap-1"
                                :class="form.scope_type === 'manual' ? 'border-primary bg-primary/10 text-primary font-bold shadow-sm' : 'border-slate-200 text-slate-600 hover:bg-slate-50'"
                            >
                                <i class="fa-solid fa-list-check text-sm"></i>
                                <span>{{ $t('label.scope_selection') }} ({{ localSelectedProductIds.length }})</span>
                            </button>
                        </div>

                        <!-- Filtro de Categorías -->
                        <div v-if="form.scope_type === 'category'" class="p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <label class="text-xs font-semibold text-slate-600 mb-1.5 block">{{ $t('label.selected_categories') }}:</label>
                            <vue-select
                                class="db-field-control f-b-custom-select bg-white"
                                v-model="form.category_ids"
                                :options="categories || []"
                                label-by="option"
                                value-by="id"
                                :multiple="true"
                                :searchable="true"
                                :closeOnSelect="false"
                                placeholder="--"
                                search-placeholder="--"
                            />
                        </div>

                        <!-- Filtro de Marcas -->
                        <div v-if="form.scope_type === 'brand'" class="p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <label class="text-xs font-semibold text-slate-600 mb-1.5 block">{{ $t('label.selected_brands') }}:</label>
                            <vue-select
                                class="db-field-control f-b-custom-select bg-white"
                                v-model="form.brand_ids"
                                :options="brands || []"
                                label-by="name"
                                value-by="id"
                                :multiple="true"
                                :searchable="true"
                                :closeOnSelect="false"
                                placeholder="--"
                                search-placeholder="--"
                            />
                        </div>

                        <!-- Filtro de Selección manual de productos -->
                        <div v-if="form.scope_type === 'manual'" class="p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div class="flex items-center justify-between mb-2">
                                <label class="text-xs font-semibold text-slate-600 block">
                                    {{ $t('label.select_products') || 'Seleccionar productos específicos' }} ({{ localSelectedProductIds.length }} seleccionados):
                                </label>
                                <div class="flex items-center gap-1 text-xs">
                                    <button type="button" @click="selectAllVisibleProducts" class="text-primary hover:underline font-medium">
                                        {{ $t('label.select_page') || 'Seleccionar página' }}
                                    </button>
                                    <span class="text-slate-300">|</span>
                                    <button type="button" @click="clearAllSelectedProducts" class="text-slate-500 hover:underline">
                                        {{ $t('button.clear') || 'Limpiar' }}
                                    </button>
                                </div>
                            </div>
                            <vue-select
                                class="db-field-control f-b-custom-select bg-white"
                                v-model="localSelectedProductIds"
                                :options="productOptions"
                                label-by="name"
                                value-by="id"
                                :multiple="true"
                                :searchable="true"
                                :closeOnSelect="false"
                                :placeholder="$t('label.search_and_select_products') || 'Buscar y seleccionar productos...'"
                                :search-placeholder="$t('label.type_product_name_or_sku') || 'Escribe el nombre o SKU del producto...'"
                            />
                            <p class="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                                <i class="fa-solid fa-circle-info text-blue-500"></i>
                                <span class="inline-block leading-none">{{ $t('label.bulk_select_table_hint') || 'También puedes marcar las casillas directamente en la tabla principal antes de abrir este modal.' }}</span>
                            </p>
                        </div>
                    </div>

                    <!-- BOTONES ACCIÓN -->
                    <div class="modal-btns pt-3 border-t border-slate-200 flex justify-end gap-2">
                        <button type="button" class="modal-btn-outline modal-close py-2 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-medium" :disabled="isSubmitting" @click="reset">
                            <span>{{ $t('button.cancel') }}</span>
                        </button>
                        <button
                            type="submit"
                            class="db-btn py-2 px-5 text-white bg-primary rounded-lg text-sm font-medium shadow hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            :disabled="isSubmitDisabled || isSubmitting"
                        >
                            <template v-if="isSubmitting">
                                <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('label.processing') || 'Procesando...' }}</span>
                            </template>
                            <template v-else>
                                <i class="fa-solid fa-bolt"></i>
                                <span>{{ $t('button.apply_update') }}</span>
                            </template>
                        </button>
                    </div>
                    </fieldset>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import appService from "../../../services/appService";
import alertService from "../../../services/alertService";

export default {
    name: "ProductBulkUpdateModal",
    props: {
        selectedProductIds: {
            type: Array,
            default: () => []
        },
        products: {
            type: Array,
            default: () => []
        },
        categories: {
            type: Array,
            default: () => []
        },
        brands: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            localSelectedProductIds: [...this.selectedProductIds],
            isSubmitting: false,
            form: {
                field: null,
                operator: "set",
                value: "",
                scope_type: "all",
                category_ids: [],
                brand_ids: [],
                product_ids: []
            }
        };
    },
    watch: {
        selectedProductIds: {
            immediate: true,
            handler(newVal) {
                const incoming = newVal || [];
                const current = this.localSelectedProductIds || [];
                if (
                    incoming.length !== current.length ||
                    !incoming.every((id, idx) => id === current[idx])
                ) {
                    this.localSelectedProductIds = [...incoming];
                }
            }
        },
        localSelectedProductIds(newVal) {
            const current = newVal || [];
            const parent = this.selectedProductIds || [];
            if (
                current.length !== parent.length ||
                !current.every((id, idx) => id === parent[idx])
            ) {
                this.$emit('update-selected-ids', current);
            }
        },
        'form.scope_type'(newScope) {
            // Limpiar las selecciones de otros alcances para garantizar que solo aplique a la opción activa
            if (newScope !== 'category') {
                this.form.category_ids = [];
            }
            if (newScope !== 'brand') {
                this.form.brand_ids = [];
            }
            if (newScope !== 'manual') {
                this.localSelectedProductIds = [];
            }
        },
        'form.field'(newVal) {
            if (newVal) {
                this.onFieldChange();
            }
        }
    },
    computed: {
        allowedFields() {
            const storeFields = this.$store.getters['bulkUpdate/allowedFields'];
            if (storeFields && Object.keys(storeFields).length > 0) {
                return storeFields;
            }
            return {
                selling_price: { label: this.$t('label.selling_price') || "Precio de Venta", type: "numeric" },
                buying_price: { label: this.$t('label.buying_price') || "Precio de Compra", type: "numeric" },
                wholesale_price: { label: this.$t('label.wholesale_price') || "Precio de Mayoreo", type: "numeric" },
                stock: { label: this.$t('label.stock') || "Stock Disponible", type: "numeric" },
                weight: { label: this.$t('label.weight_kg') || "Peso (kg)", type: "numeric" },
                length: { label: this.$t('label.length_cm') || "Largo (cm)", type: "numeric" },
                width: { label: this.$t('label.width_cm') || "Ancho (cm)", type: "numeric" },
                height: { label: this.$t('label.height_cm') || "Alto (cm)", type: "numeric" },
                status: { label: this.$t('label.product_status') || "Estado del Producto", type: "enum_status" },
                can_purchasable: { label: this.$t('label.can_purchasable') || "Comprable", type: "enum_ask" },
                show_stock_out: { label: this.$t('label.show_stock_out') || "Mostrar si Agotado", type: "enum_ask" },
                refundable: { label: this.$t('label.refundable') || "Reembolsable", type: "enum_ask" },
                maximum_purchase_quantity: { label: this.$t('label.maximum_purchase_quantity') || "Límite Máximo de Compra", type: "numeric" },
                low_stock_quantity_warning: { label: this.$t('label.low_stock_quantity_warning') || "Alerta de Stock Bajo", type: "numeric" },
                warranty: { label: this.$t('label.warranty') || "Garantía", type: "text" }
            };
        },
        fieldOptions() {
            return [
                { id: "selling_price", name: this.$t('label.selling_price') || "Precio de Venta" },
                { id: "buying_price", name: this.$t('label.buying_price') || "Precio de Compra" },
                { id: "wholesale_price", name: this.$t('label.wholesale_price') || "Precio de Mayoreo" },
                { id: "stock", name: this.$t('label.stock') || "Stock" },
                { id: "maximum_purchase_quantity", name: this.$t('label.maximum_purchase_quantity') || "Límite Máximo de Compra" },
                { id: "low_stock_quantity_warning", name: this.$t('label.low_stock_quantity_warning') || "Alerta de Stock Bajo" },
                { id: "weight", name: this.$t('label.weight_kg') || "Peso (kg)" },
                { id: "length", name: this.$t('label.length_cm') || "Largo (cm)" },
                { id: "width", name: this.$t('label.width_cm') || "Ancho (cm)" },
                { id: "height", name: this.$t('label.height_cm') || "Alto (cm)" },
                { id: "status", name: this.$t('label.product_status') || this.$t('label.status') || "Estado" },
                { id: "can_purchasable", name: this.$t('label.can_purchasable_yes_no') || "Comprable (Sí / No)" },
                { id: "show_stock_out", name: this.$t('label.show_stock_out') || "Mostrar si está agotado" },
                { id: "refundable", name: this.$t('label.refundable') || "Reembolsable" },
                { id: "warranty", name: this.$t('label.warranty') || "Garantía" }
            ];
        },
        numericOperators() {
            return [
                { id: 'set', name: this.$t('label.exact_value') || 'Valor exacto (=)' },
                { id: 'add', name: this.$t('label.add_fixed') || 'Sumar monto (+)' },
                { id: 'subtract', name: this.$t('label.subtract_fixed') || 'Restar monto (-)' },
                { id: 'percent_add', name: this.$t('label.increase_percent') || 'Aumentar porcentaje (+%)' },
                { id: 'percent_sub', name: this.$t('label.decrease_percent') || 'Disminuir porcentaje (-%)' }
            ];
        },
        statusOptions() {
            return [
                { id: 'active', name: this.$t('label.active') || 'Activo' },
                { id: 'inactive', name: this.$t('label.inactive') || 'Inactivo' }
            ];
        },
        yesNoOptions() {
            return [
                { id: 'yes', name: this.$t('label.yes') || 'Sí' },
                { id: 'no', name: this.$t('label.no') || 'No' }
            ];
        },
        currentFieldType() {
            if (!this.form.field) return null;
            return this.allowedFields[this.form.field]?.type || 'numeric';
        },
        isPercentageOperator() {
            return this.form.operator === 'percent_add' || this.form.operator === 'percent_sub';
        },
        fieldLabel() {
            return this.allowedFields[this.form.field]?.label || this.form.field;
        },
        operatorDisplay() {
            if (this.currentFieldType === 'enum_status') {
                return this.form.value === 'active' ? (this.$t('label.active') || 'Activo') : (this.$t('label.inactive') || 'Inactivo');
            }
            if (this.currentFieldType === 'enum_ask') {
                return this.form.value === 'yes' ? (this.$t('label.yes') || 'Sí') : (this.$t('label.no') || 'No');
            }
            if (this.currentFieldType === 'text') {
                return `"${this.form.value}"`;
            }
            switch (this.form.operator) {
                case 'add': return `+ ${this.form.value}`;
                case 'subtract': return `- ${this.form.value}`;
                case 'percent_add': return `+${this.form.value}%`;
                case 'percent_sub': return `-${this.form.value}%`;
                case 'set':
                default: return `= ${this.form.value}`;
            }
        },
        productOptions() {
            return (this.products || []).map(p => ({
                id: p.id,
                name: `${p.name} ${p.sku ? '(' + p.sku + ')' : ''}`
            }));
        },
        scopeLabel() {
            switch (this.form.scope_type) {
                case 'category': return `${this.form.category_ids.length} ${this.$t('label.categories_selected') || 'categorías seleccionadas'}`;
                case 'brand': return `${this.form.brand_ids.length} ${this.$t('label.brands_selected') || 'marcas seleccionadas'}`;
                case 'all': return this.$t('label.all_products_catalog') || 'Todo el catálogo de productos';
                case 'manual':
                default: return `${this.localSelectedProductIds.length} ${this.$t('label.products_selected') || 'productos seleccionados'}`;
            }
        },
        isSubmitDisabled() {
            if (!this.form.field) return true;
            if (this.form.value === null || this.form.value === "") return true;
            if (this.form.scope_type === 'manual' && this.localSelectedProductIds.length === 0) return true;
            if (this.form.scope_type === 'category' && this.form.category_ids.length === 0) return true;
            if (this.form.scope_type === 'brand' && this.form.brand_ids.length === 0) return true;
            return false;
        }
    },
    methods: {
        selectAllVisibleProducts() {
            const visibleIds = (this.products || []).map(p => p.id);
            this.localSelectedProductIds = [...new Set([...this.localSelectedProductIds, ...visibleIds])];
        },
        clearAllSelectedProducts() {
            this.localSelectedProductIds = [];
        },
        reset() {
            if (this.isSubmitting) return;
            appService.modalHide();
            this.isSubmitting = false;
            this.form = {
                field: null,
                operator: "set",
                value: "",
                scope_type: "all",
                category_ids: [],
                brand_ids: [],
                product_ids: []
            };
            this.localSelectedProductIds = [];
        },
        onFieldChange() {
            const type = this.currentFieldType;
            if (type === 'enum_status') {
                this.form.operator = 'set';
                this.form.value = 'active';
            } else if (type === 'enum_ask') {
                this.form.operator = 'set';
                this.form.value = 'yes';
            } else if (type === 'text') {
                this.form.operator = 'set';
                this.form.value = '';
            } else {
                this.form.operator = 'set';
                this.form.value = '';
            }
        },
        confirmSubmit() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;

            const payload = {
                field: this.form.field,
                operator: this.form.operator,
                value: this.form.value,
                scope_type: this.form.scope_type,
                category_ids: this.form.category_ids,
                brand_ids: this.form.brand_ids,
                product_ids: this.localSelectedProductIds
            };

            this.$emit('submit-batch', {
                payload,
                onDone: () => {
                    this.isSubmitting = false;
                    this.reset();
                },
                onError: () => {
                    this.isSubmitting = false;
                }
            });
        }
    }
};
</script>

<style scoped>
.centered-modal {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 1rem;
}

.centered-modal .modal-dialog {
    margin: auto !important;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

/* .centered-modal .modal-body {
} */
</style>

